import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Send,
  Image,
  User,
  Shield,
  Clock,
  MessageSquare,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Zap,
  Search,
  Star,
  ChevronDown,
  RefreshCw,
  UserCheck,
  Trash2,
  Copy,
  Edit2
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { chatService, ChatRoom, ChatMessage } from '@/services/chatService'
import { apiService } from '@/services/api'
import chatAutoCloseSettingsService from '@/services/chatAutoCloseSettingsService'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import MissionRankBadge from '@/components/common/MissionRankBadge'

interface LiveChatTemplate {
  id: number
  name: string
  title: string
  content: string
  is_favorite: boolean
}

interface ChatDetailModalProps {
  isOpen: boolean
  onClose: () => void
  room: ChatRoom | null
  onUpdate?: () => void
}

const ChatDetailModal = ({ isOpen, onClose, room, onUpdate }: ChatDetailModalProps) => {
  const { t } = useTranslation(['common', 'modals', 'services'])
  const navigate = useNavigate()
  const goToUser = () => {
    if (room?.user_id) { onClose(); navigate(`/admin/users/${room.user_id}`) }
  }
  const [messages, setMessages] = useState<ChatMessage[]>([])
  // 제목 인라인 수정 (2026-07-29 사장님)
  const [titleEditing, setTitleEditing] = useState(false)
  const [titleText, setTitleText] = useState('')
  const [titleSaving, setTitleSaving] = useState(false)
  const [titleOverride, setTitleOverride] = useState<string | null>(null)
  // 메시지 인라인 수정 (2026-07-29 — 관리자 메시지만)
  const [editingMsg, setEditingMsg] = useState<{ id: number; text: string } | null>(null)
  useEffect(() => { setTitleOverride(null); setTitleEditing(false) }, [room?.id])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [isClosed, setIsClosed] = useState(false)
  // 방 상태를 로컬에서 추적 (승인 직후 입력 UI 표시를 위해)
  const [currentStatus, setCurrentStatus] = useState<string>('')
  const [assigning, setAssigning] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WebSocket | null>(null)

  // 미니 템플릿 패널 상태
  const [showTemplates, setShowTemplates] = useState(false)
  const [liveChatTemplates, setLiveChatTemplates] = useState<LiveChatTemplate[]>([])
  const [templateSearch, setTemplateSearch] = useState('')
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templatesLoaded, setTemplatesLoaded] = useState(false)

  // 메시지 우클릭/롱프레스 컨텍스트 메뉴 (PC 우클릭, 모바일 꾹누르기)
  const [contextMenu, setContextMenu] = useState<{
    messageId: number
    x: number
    y: number
  } | null>(null)
  const longPressTimerRef = useRef<number | null>(null)
  const longPressTriggeredRef = useRef(false)
  const contextMenuRef = useRef<HTMLDivElement | null>(null)

  // Get token from localStorage or cookie
  const getToken = () => {
    // Try localStorage first (use same key as API service)
    const localToken = localStorage.getItem('admin_token')
    if (localToken) return localToken

    // Try cookie as fallback
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === 'adminToken' || name === 'admin_token') return value
    }
    return null
  }

  const token = getToken()

  // 5분뒤 자동 종료 arm 상태
  const [armedAt, setArmedAt] = useState<string | null>(null)
  const [arming, setArming] = useState(false)

  // 모달이 열릴 때 배경 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // 방 변경/모달 오픈 시 armed 상태 동기화
  // - room prop 의 armed_at 으로 1차 초기화 (즉시성)
  // - 서버에서 최신값을 다시 가져와 보정 (모달 닫혀있는 사이 사용자 답장으로 disarm 됐을 수 있음)
  useEffect(() => {
    if (!room || !isOpen) return
    setArmedAt(room.auto_close_armed_at ?? null)
    let cancelled = false
    chatAutoCloseSettingsService.getState(room.id)
      .then((s) => {
        if (cancelled) return
        setArmedAt(s.armed_at ?? null)
      })
      .catch(() => { /* 실패 시 prop 값으로 그대로 둠 */ })
    return () => { cancelled = true }
  }, [room?.id, isOpen])

  // 5분뒤 종료 버튼 — arm 호출
  const handleArmAutoClose = async () => {
    if (!room || arming || armedAt) return
    setArming(true)
    try {
      const resp = await chatAutoCloseSettingsService.arm(room.id)
      setArmedAt(resp.armed_at)
      toast.success(t('services:chatAutoClose.armed'))
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || t('services:chatAutoClose.armFailed')
      toast.error(typeof msg === 'string' ? msg : t('services:chatAutoClose.armFailed'))
    } finally {
      setArming(false)
    }
  }

  useEffect(() => {
    if (room && isOpen) {
      setIsClosed(room.status === 'CLOSED')
      setCurrentStatus(room.status)
      loadMessages()
      connectWebSocket()
      // 모달 열 때 템플릿 미리 로드
      if (!templatesLoaded) {
        loadLiveChatTemplates()
      }
      // 카톡 스타일 안읽음 배지 클리어 — 관리자가 채팅방 열면 읽음 처리
      chatService.markRoomAsRead(room.id)
        .then(() => onUpdate?.())
        .catch((err) => console.warn('읽음 처리 실패:', err))
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [room, isOpen])

  // 모달 닫힐 때 템플릿 패널 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setShowTemplates(false)
      setTemplateSearch('')
      setTemplatesLoaded(false)
    }
  }, [isOpen])

  // 메시지가 업데이트될 때마다 맨 아래로 스크롤
  useEffect(() => {
    if (messages.length > 0) {
      // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 스크롤
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [messages])

  // 실시간 템플릿 로드
  const loadLiveChatTemplates = async () => {
    try {
      setTemplatesLoading(true)
      const client = apiService.getClient()
      const resp = await client.get('/api/v1/admin/templates', {
        params: {
          template_type: 'LIVE_CHAT',
          is_active: true,
          limit: 100,
          page: 1
        }
      })
      const data = resp.data?.data || resp.data || []
      setLiveChatTemplates(Array.isArray(data) ? data : [])
      setTemplatesLoaded(true)
    } catch (error) {
      console.error('실시간 템플릿 로드 실패:', error)
    } finally {
      setTemplatesLoading(false)
    }
  }

  // 필터링된 템플릿 (즐겨찾기 우선 + 검색)
  const filteredTemplates = useMemo(() => {
    let filtered = liveChatTemplates
    if (templateSearch) {
      const search = templateSearch.toLowerCase()
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(search) ||
        t.content.toLowerCase().includes(search)
      )
    }
    return [...filtered].sort((a, b) => {
      if (a.is_favorite && !b.is_favorite) return -1
      if (!a.is_favorite && b.is_favorite) return 1
      return 0
    })
  }, [liveChatTemplates, templateSearch])

  // 템플릿 클릭 시 메시지 입력란에 삽입
  const handleInsertTemplate = (content: string) => {
    setMessageText(prev => prev ? prev + ' ' + content : content)
  }

  const loadMessages = async () => {
    if (!room) return

    try {
      setLoading(true)

      // 모든 메시지를 가져오기 (페이지네이션 자동 처리)
      const allMessages = await chatService.getAllMessages(room.id)

      setMessages(allMessages)

      // Scroll to bottom after loading messages
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    } catch (error) {
      console.error('메시지 로딩 실패:', error)
      console.error('에러 상세:', (error as any).response?.data || error)
      toast.error(t('modals:chatDetail.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const connectWebSocket = () => {
    if (!room || !token) return

    const ws = chatService.connectWebSocket(room.id, token)

    // 30초 주기 ping — Cloudflare 100s idle 타임아웃 방지
    let heartbeat: number | null = null
    const startHeartbeat = () => {
      if (heartbeat !== null) window.clearInterval(heartbeat)
      heartbeat = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ type: 'ping' }))
          } catch {
            // 전송 실패는 onclose가 따라옴 — 무시
          }
        }
      }, 30000)
    }
    const stopHeartbeat = () => {
      if (heartbeat !== null) {
        window.clearInterval(heartbeat)
        heartbeat = null
      }
    }

    ws.onopen = () => {
      console.log('WebSocket 연결됨')
      startHeartbeat()
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'chat_message') {
          setMessages(prev => {
            // 중복 메시지 방지: 이미 같은 ID의 메시지가 있으면 추가하지 않음
            const messageExists = prev.some(msg => msg.id === data.data.id)
            if (messageExists) {
              return prev
            }
            // 새 메시지를 추가하고 날짜 순서대로 정렬
            const newMessages = [...prev, data.data]
            return newMessages.sort((a, b) =>
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            )
          })
          // 스크롤은 useEffect에서 처리
          // 모달 열려있는 동안 사용자/게스트 새 메시지 도착 → 즉시 읽음 처리
          if (room && (data.data?.sender_type === 'user' || data.data?.sender_type === 'guest')) {
            chatService.markRoomAsRead(room.id)
              .then(() => onUpdate?.())
              .catch(() => {})
          }
        } else if (data.type === 'message_updated') {
          const uid = data.data?.message_id
          const ucontent = data.data?.content
          if (uid) {
            setMessages(prev => prev.map(msg => msg.id === uid ? { ...msg, content: ucontent } : msg))
          }
        } else if (data.type === 'message_deleted') {
          // 다른 관리자가 삭제했거나 본인이 삭제한 메시지 — 화면에서 제거
          const deletedId = data.data?.message_id
          if (deletedId) {
            setMessages(prev => prev.filter(msg => msg.id !== deletedId))
          }
        } else if (data.type === 'messages_read') {
          // 사용자가 관리자 메시지를 읽음 — 카톡식 '1' 실시간 제거 (2026-08-14)
          const readAt = data.data?.read_at || new Date().toISOString()
          setMessages(prev => prev.map(msg =>
            (msg.sender_type === 'admin' && !msg.is_read) ? { ...msg, is_read: true, read_at: readAt } : msg
          ))
        } else if (data.type === 'pong') {
          // 백엔드 keepalive 응답 — 무시 (연결 유지 자체가 목적)
        } else if (data.type === 'room_closed') {
          // 채팅방 종료 시 UI 업데이트 (백엔드에서 이미 시스템 메시지를 보내므로 여기서는 추가하지 않음)
          setIsClosed(true)
          setArmedAt(null)
          toast(t('modals:userTest.chatRoomClosed'))
          {t('modals:ChatDetail.k_0d9f03')}
        } else if (data.type === 'auto_close_armed') {
          // 5분뒤 종료가 set 됨 (본인이 눌렀거나 같은 방을 보는 다른 관리자)
          const at = data?.data?.armed_at ?? null
          setArmedAt(at)
        } else if (data.type === 'auto_close_disarmed') {
          // 사용자가 답장 → 5분뒤 종료 해제 (실시간)
          setArmedAt(null)
          toast(t('services:chatAutoClose.disarmed'))
        }
      } catch (error) {
        console.error('WebSocket 메시지 파싱 실패:', error)
      }
    }

    ws.onerror = (error) => {
      console.error('WebSocket 에러:', error)
    }

    ws.onclose = () => {
      console.log('WebSocket 연결 종료')
      stopHeartbeat()
    }

    wsRef.current = ws
  }

  // ───── 메시지 삭제 컨텍스트 메뉴 ─────
  // PC: 우클릭 (onContextMenu), 모바일: 꾹누르기 (~500ms)
  const openContextMenu = (messageId: number, x: number, y: number) => {
    // 화면 가장자리 잘림 방지 — 메뉴 폭/높이 대략 160x92 (복사+삭제), 살짝 여유두고 위치 보정
    const menuW = 160
    const menuH = 92
    const adjustedX = Math.min(x, window.innerWidth - menuW - 8)
    const adjustedY = Math.min(y, window.innerHeight - menuH - 8)
    setContextMenu({ messageId, x: adjustedX, y: adjustedY })
  }

  const closeContextMenu = () => setContextMenu(null)

  const handleMessageContextMenu = (e: React.MouseEvent, messageId: number) => {
    e.preventDefault()
    openContextMenu(messageId, e.clientX, e.clientY)
  }

  const handleMessageTouchStart = (e: React.TouchEvent, messageId: number) => {
    longPressTriggeredRef.current = false
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current)
    }
    const touch = e.touches[0]
    const x = touch.clientX
    const y = touch.clientY
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true
      // 햅틱 피드백 (지원 디바이스에서만)
      try { navigator.vibrate?.(15) } catch {}
      openContextMenu(messageId, x, y)
    }, 500)
  }

  const handleMessageTouchEnd = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }

  const handleMessageTouchMove = () => {
    // 손가락이 움직이면 long-press 취소 (스크롤 의도로 간주)
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }

  const handleCopyMessage = async () => {
    if (!contextMenu) return
    const messageId = contextMenu.messageId
    const target = messages.find(m => m.id === messageId)
    closeContextMenu()
    if (!target) return

    const text = target.message_type === 'IMAGE'
      ? (target.image_url ? `${(import.meta as any).env.VITE_API_BASE_URL || 'https://api.kryzonic.com'}${target.image_url}` : '')
      : (target.content || '')

    if (!text) {
      toast.error(t('modals:chatDetail.nothingToCopy', '복사할 내용이 없습니다'))
      return
    }

    const fallbackCopy = (value: string) => {
      const ta = document.createElement('textarea')
      ta.value = value
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      ta.setAttribute('readonly', '')
      document.body.appendChild(ta)
      ta.select()
      let ok = false
      try { ok = document.execCommand('copy') } catch { ok = false }
      document.body.removeChild(ta)
      return ok
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
      } else if (!fallbackCopy(text)) {
        throw new Error('execCommand copy failed')
      }
      toast.success(t('modals:chatDetail.messageCopied', '메시지를 복사했습니다'))
    } catch (error) {
      console.error('메시지 복사 실패:', error)
      if (fallbackCopy(text)) {
        toast.success(t('modals:chatDetail.messageCopied', '메시지를 복사했습니다'))
      } else {
        toast.error(t('modals:chatDetail.messageCopyFailed', '메시지 복사에 실패했습니다'))
      }
    }
  }

  const handleDeleteMessage = async () => {
    if (!contextMenu || !room) return
    const messageId = contextMenu.messageId
    closeContextMenu()

    // 즉시 로컬에서 제거 (낙관적 업데이트). 실패 시 다시 로드.
    const snapshot = messages
    setMessages(prev => prev.filter(msg => msg.id !== messageId))

    try {
      await chatService.deleteMessage(room.id, messageId)
      toast.success(t('modals:chatDetail.messageDeleted', '메시지를 삭제했습니다'))
    } catch (error) {
      console.error('메시지 삭제 실패:', error)
      toast.error(t('modals:chatDetail.messageDeleteFailed', '메시지 삭제에 실패했습니다'))
      // 실패 시 원본 복원
      setMessages(snapshot)
    }
  }

  // 컨텍스트 메뉴 외부 클릭/터치 시 닫기 (PC + 모바일)
  // capture phase로 등록 — 메뉴 자체의 stopPropagation도 우회
  // ref 기반 outside 검사 — 메뉴 안 클릭이면 닫지 않음
  useEffect(() => {
    if (!contextMenu) return

    const isInsideMenu = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node)) return false
      return !!contextMenuRef.current && contextMenuRef.current.contains(target)
    }

    const handlePointerDown = (e: Event) => {
      if (!isInsideMenu(e.target)) {
        closeContextMenu()
      }
    }
    const handleContextMenu = (e: Event) => {
      // 다른 곳 우클릭하면 현재 메뉴 닫고 새 위치로 다시 열림 (handleMessageContextMenu가 처리)
      if (!isInsideMenu(e.target)) {
        closeContextMenu()
      }
    }
    const handleScroll = () => closeContextMenu()
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeContextMenu() }

    // 다음 tick에 리스너 등록 — 메뉴를 연 그 이벤트 자체가 즉시 close 트리거하지 않도록
    const tid = window.setTimeout(() => {
      // capture phase로 등록 — 모달이나 stopPropagation을 우회해서 항상 도달
      window.addEventListener('mousedown', handlePointerDown, true)
      window.addEventListener('touchstart', handlePointerDown, true)
      window.addEventListener('contextmenu', handleContextMenu, true)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('keydown', handleKey)
    }, 0)

    return () => {
      window.clearTimeout(tid)
      window.removeEventListener('mousedown', handlePointerDown, true)
      window.removeEventListener('touchstart', handlePointerDown, true)
      window.removeEventListener('contextmenu', handleContextMenu, true)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('keydown', handleKey)
    }
  }, [contextMenu])

  // 모달 닫힐 때 long-press 타이머/메뉴 정리
  useEffect(() => {
    if (!isOpen) {
      if (longPressTimerRef.current !== null) {
        window.clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
      setContextMenu(null)
    }
  }, [isOpen])

  const handleSendMessage = async () => {
    if (!messageText.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return
    }

    try {
      setSending(true)

      // Send message through WebSocket
      wsRef.current.send(JSON.stringify({
        type: 'chat_message',
        content: messageText
      }))

      setMessageText('')
    } catch (error) {
      console.error('메시지 전송 실패:', error)
      toast.error(t('content:chat.sendFailed'))
    } finally {
      setSending(false)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !room) return

    try {
      setSending(true)
      await chatService.uploadImage(room.id, file)
      toast.success(t('modals:chatDetail.imageSent'))
      // 이미지 업로드 후 WebSocket을 통해 메시지가 올 것이므로 잠시 대기
      // WebSocket이 연결되어 있지 않거나 메시지가 오지 않으면 다시 로드
      setTimeout(async () => {
        await loadMessages()
      }, 500)
    } catch (error) {
      console.error('이미지 업로드 실패:', error)
      toast.error(t('modals:chatDetail.imageUploadFailed'))
    } finally {
      setSending(false)
    }
  }

  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false)
  const [closeLoading, setCloseLoading] = useState(false)

  const handleCloseRoom = async () => {
    if (!room || closeLoading) return
    setCloseLoading(true)
    try {
      await chatService.closeChatRoom(room.id)
      toast.success(t('modals:userTest.chatRoomClosed'))
      setCloseConfirmOpen(false)
      onUpdate?.()
      onClose()
    } catch (error) {
      console.error('채팅방 종료 실패:', error)
      toast.error(t('modals:userTest.chatRoomCloseFailed'))
    } finally {
      setCloseLoading(false)
    }
  }

  // 채팅방 배정(승인) — 모달 안에서 바로 수행
  const handleAssignRoom = async () => {
    if (!room || assigning) return
    try {
      setAssigning(true)
      await chatService.assignChatRoom(room.id)
      toast.success(t('modals:userTest.chatRoomAssigned', '채팅방이 배정되었습니다'))
      // 로컬 상태 갱신 → 입력 UI 노출
      setCurrentStatus('ACTIVE')
      // 부모 리스트 갱신 (대기 카운트 등)
      onUpdate?.()
      // 메시지 새로고침
      await loadMessages()
    } catch (error) {
      console.error('채팅방 배정 실패:', error)
      toast.error(t('modals:userTest.chatRoomAssignFailed', '채팅방 배정에 실패했습니다'))
    } finally {
      setAssigning(false)
    }
  }

  // 종료된 채팅방 다시 배정
  const handleReassignRoom = async () => {
    if (!room || assigning) return
    try {
      setAssigning(true)
      await chatService.reassignChatRoom(room.id)
      toast.success('채팅방이 다시 배정되었습니다')
      setIsClosed(false)
      setCurrentStatus('ACTIVE')
      onUpdate?.()
      await loadMessages()
    } catch (error) {
      console.error('채팅방 재배정 실패:', error)
      toast.error('채팅방 재배정에 실패했습니다')
    } finally {
      setAssigning(false)
    }
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      WAITING: { label: t('content:inquiries.statusWaiting'), color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
      ACTIVE: { label: t('modals:userTest.inProgress'), color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      CLOSED: { label: t('modals:userTest.end'), color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.WAITING
    const IconComponent = config.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    )
  }

  if (!isOpen || !room) return null

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-[99999] p-0 sm:p-4"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100dvh',
          margin: 0
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="glass-card max-w-4xl w-full h-[100dvh] sm:h-[80vh] sm:max-h-[90vh] flex flex-col overflow-hidden rounded-none sm:rounded-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-2 sm:p-6 border-b border-white/10 flex-shrink-0">
            {/* Row 1: 제목 + 닫기(X) */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  {titleEditing ? (
                    <div className="flex items-center gap-1.5">
                      <input autoFocus value={titleText} maxLength={100}
                        onChange={e => setTitleText(e.target.value)}
                        onKeyDown={async e => {
                          if (e.key === 'Escape') setTitleEditing(false)
                          if (e.key === 'Enter' && titleText.trim() && !titleSaving) {
                            try {
                              setTitleSaving(true)
                              await chatService.updateChatTitle(room.id, titleText.trim())
                              setTitleOverride(titleText.trim())
                              setTitleEditing(false)
                              onUpdate?.()
                            } catch (err) { console.error('제목 수정 실패:', err) }
                            finally { setTitleSaving(false) }
                          }
                        }}
                        onBlur={() => setTitleEditing(false)}
                        className="flex-1 min-w-0 px-2 py-1 rounded-lg bg-white/10 border border-primary-500/50
                                   text-sm sm:text-lg font-semibold text-white outline-none" />
                      <span className="text-[10px] text-dark-400 shrink-0 hidden sm:inline">Enter=저장</span>
                    </div>
                  ) : (
                    <h3
                      title="클릭해서 제목 수정"
                      onClick={() => { setTitleText(titleOverride ?? room.title); setTitleEditing(true) }}
                      className="text-sm sm:text-xl font-semibold text-white truncate leading-tight
                                 cursor-pointer hover:text-primary-300"
                    >{titleOverride ?? room.title} <span className="text-[10px] text-dark-500 font-normal">✎</span></h3>
                  )}
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1 min-w-0 flex-wrap">
                    <span
                      className={`text-[11px] sm:text-sm text-dark-400 truncate ${room.user_id ? 'cursor-pointer hover:text-primary-400 transition-colors' : ''}`}
                      onClick={goToUser}
                    >{room.user_name}</span>
                    <MissionRankBadge userId={room.user_id} size="sm" showLabel />
                    <span className="text-[11px] sm:text-sm text-dark-500 hidden sm:inline">&bull;</span>
                    <span
                      className={`text-[11px] sm:text-sm text-dark-400 truncate hidden sm:inline ${room.user_id ? 'cursor-pointer hover:text-primary-400 transition-colors' : ''}`}
                      onClick={goToUser}
                    >{room.user_email}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="glass-button p-1.5 sm:p-2 rounded-lg flex-shrink-0"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
            {/* Row 2: 상태 뱃지 + 액션 버튼 (승인/채팅종료) */}
            <div className="flex items-center justify-between mt-2 gap-2">
              <div className="flex-shrink-0">
                {getStatusBadge(currentStatus || room.status)}
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                {/* 배정(승인) 버튼 — WAITING 상태에서만 노출 */}
                {!isClosed && currentStatus === 'WAITING' && (
                  <button
                    onClick={handleAssignRoom}
                    disabled={assigning}
                    className="btn-success py-1.5 px-3 sm:py-2 sm:px-4 text-xs sm:text-sm flex items-center gap-1 disabled:opacity-50"
                    title={t('Chat.k_a35927', '채팅 배정')}
                  >
                    <UserCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="hidden sm:inline">{t('Chat.k_a35927', '채팅 배정')}</span>
                    <span className="sm:hidden">승인</span>
                  </button>
                )}
                {!isClosed && currentStatus !== 'WAITING' && (
                  <button
                    onClick={handleArmAutoClose}
                    disabled={arming || !!armedAt}
                    title={armedAt
                      ? t('services:chatAutoClose.armWaitingTitle')
                      : t('services:chatAutoClose.armTitle')}
                    className={`py-1.5 px-3 sm:py-2 sm:px-4 text-xs sm:text-sm rounded-lg font-medium flex items-center gap-1 transition-colors ${
                      armedAt
                        ? 'bg-dark-700 text-dark-400 border border-dark-600 cursor-not-allowed'
                        : 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/40 hover:bg-yellow-500/25'
                    }`}
                  >
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="hidden sm:inline">
                      {armedAt ? t('services:chatAutoClose.armBtnWaiting') : t('services:chatAutoClose.armBtn')}
                    </span>
                    <span className="sm:hidden">
                      {armedAt ? t('services:chatAutoClose.armShortWaiting') : t('services:chatAutoClose.armShort')}
                    </span>
                  </button>
                )}
                {!isClosed && currentStatus !== 'WAITING' && (
                  <button
                    onClick={() => setCloseConfirmOpen(true)}
                    className="btn-error py-1.5 px-3 sm:py-2 sm:px-4 text-xs sm:text-sm"
                  >
                    <span className="hidden sm:inline">{t('modals:userTest.endChat')}</span>
                    <span className="sm:hidden">{t('modals:userTest.end')}</span>
                  </button>
                )}
                {isClosed && (
                  <button
                    onClick={handleReassignRoom}
                    disabled={assigning}
                    className="btn-primary py-1.5 px-3 sm:py-2 sm:px-4 text-xs sm:text-sm flex items-center gap-1 disabled:opacity-50"
                    title="다시 배정받기"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 ${assigning ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">다시 배정받기</span>
                    <span className="sm:hidden">재배정</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 min-h-0 overflow-y-auto p-2 sm:p-6 space-y-3 sm:space-y-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="spinner w-8 h-8"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <MessageSquare className="w-12 h-12 text-dark-600 mb-4" />
                <p className="text-dark-400">{t('modals:chatDetail.noMessages')}</p>
              </div>
            ) : (
              <>
                {messages.map((message, index) => {
                  const isAdmin = message.sender_type === 'admin'
                  const isSystem = message.sender_type === 'system' && message.message_type === 'SYSTEM'
                  const isUser = message.sender_type === 'user' || (message.sender_type as string) === 'guest'

                  // Show date separator if date changes
                  const showDateSeparator = index === 0 ||
                    formatDate(message.created_at) !== formatDate(messages[index - 1].created_at)

                  return (
                    <div key={message.id}>
                      {showDateSeparator && (
                        <div className="flex items-center justify-center my-4">
                          <div className="glass-dark px-3 py-1 rounded-full">
                            <span className="text-xs text-dark-400">
                              {formatDate(message.created_at)}
                            </span>
                          </div>
                        </div>
                      )}

                      {isSystem ? (
                        <div className="flex justify-center">
                          <div className="glass-dark px-4 py-2 rounded-full max-w-md">
                            <div className="flex items-center space-x-2">
                              <AlertTriangle className="w-4 h-4 text-yellow-400" />
                              <span className="text-sm text-yellow-400">{message.content}</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs sm:max-w-md ${isAdmin ? 'items-end' : 'items-start'} flex flex-col space-y-1`}>
                            <div className="flex items-center space-x-2 px-1">
                              {isAdmin ? (
                                <Shield className="w-4 h-4 text-primary-400" />
                              ) : (
                                <User className="w-4 h-4 text-dark-400" />
                              )}
                              <span className="text-xs text-dark-400 truncate">{message.sender_name}</span>
                              <span className="text-xs text-dark-500">{formatTime(message.created_at)}</span>
                              {/* 사용자 읽음 표시 (관리자→사용자 메시지) — 카톡식: 안읽음=노란 '1', 읽음='읽음 HH:MM' */}
                              {isAdmin && (message.message_type === 'TEXT' || message.message_type === 'IMAGE') && (
                                message.is_read
                                  ? <span className="text-[10px] text-emerald-400" title={message.read_at ? `읽은 시각: ${formatTime(message.read_at)}` : '읽음'}>읽음{message.read_at ? ` ${formatTime(message.read_at)}` : ''}</span>
                                  : <span className="text-[10px] font-bold text-amber-400" title="사용자가 아직 읽지 않음">1</span>
                              )}
                            </div>

                            {message.message_type === 'IMAGE' ? (
                              <div
                                className="glass-dark p-2 rounded-xl max-w-full select-none cursor-context-menu"
                                onContextMenu={(e) => handleMessageContextMenu(e, message.id)}
                                onTouchStart={(e) => handleMessageTouchStart(e, message.id)}
                                onTouchEnd={handleMessageTouchEnd}
                                onTouchMove={handleMessageTouchMove}
                                onTouchCancel={handleMessageTouchEnd}
                              >
                                <img
                                  src={message.image_url ? `${(import.meta as any).env.VITE_API_BASE_URL || 'https://api.kryzonic.com'}${message.image_url}` : '#'}
                                  alt={message.image_filename || 'Image'}
                                  className="max-w-full rounded-lg h-auto"
                                  draggable={false}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none'
                                    const errorDiv = document.createElement('div')
                                    errorDiv.className = 'text-xs text-gray-400 p-2'
                                    errorDiv.textContent = t('modals:userTest.imageLoadFailed')
                                    e.currentTarget.parentNode?.appendChild(errorDiv)
                                  }}
                                />
                              </div>
                            ) : (
                              <div
                                className={`px-3 sm:px-4 py-2 sm:py-3 rounded-xl max-w-full select-none cursor-context-menu ${
                                  isAdmin
                                    ? 'bg-primary-500/20 text-white border border-primary-500/30'
                                    : 'glass-dark text-white'
                                }`}
                                onContextMenu={(e) => handleMessageContextMenu(e, message.id)}
                                onTouchStart={(e) => handleMessageTouchStart(e, message.id)}
                                onTouchEnd={handleMessageTouchEnd}
                                onTouchMove={handleMessageTouchMove}
                                onTouchCancel={handleMessageTouchEnd}
                              >
                                {editingMsg?.id === message.id ? (
                                  <div className="min-w-[220px]">
                                    <textarea autoFocus value={editingMsg.text} rows={3} maxLength={2000}
                                      onChange={e => setEditingMsg({ id: message.id, text: e.target.value })}
                                      className="w-full p-2 rounded-lg bg-black/30 border border-primary-400/50
                                                 text-sm text-white outline-none resize-none whitespace-pre-wrap" />
                                    <div className="flex justify-end gap-1.5 mt-1">
                                      <button onClick={() => setEditingMsg(null)}
                                        className="px-2 py-1 text-xs rounded bg-white/10 text-dark-300 hover:bg-white/20">
                                        {t('common:cancel', '취소')}</button>
                                      <button
                                        onClick={async () => {
                                          if (!room || !editingMsg.text.trim()) return
                                          try {
                                            await chatService.updateMessage(room.id, message.id, editingMsg.text.trim())
                                            setMessages(prev => prev.map(m => m.id === message.id
                                              ? { ...m, content: editingMsg.text.trim() } : m))
                                            setEditingMsg(null)
                                          } catch (err) { console.error('메시지 수정 실패:', err) }
                                        }}
                                        className="px-2 py-1 text-xs rounded bg-primary-500 text-white hover:bg-primary-600">
                                        {t('common:save', '저장')}</button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Mini Template Panel */}
          {!isClosed && currentStatus !== 'WAITING' && (
            <AnimatePresence>
              {showTemplates && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="border-t border-white/10 overflow-hidden flex-shrink-0"
                >
                  <div className="p-2 sm:p-3" style={{ maxHeight: '30vh', overflowY: 'auto' }}>
                    {/* 검색바 + 새로고침 + 닫기 */}
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-3.5 sm:h-3.5 text-dark-400" />
                        <input
                          type="text"
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                          placeholder={t('modals:chatDetail.searchTemplate')}
                          className="w-full pl-7 pr-2 py-1.5 text-xs bg-white/5 border border-white/10 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-1 focus:ring-primary-500/50"
                        />
                      </div>
                      <button
                        onClick={() => loadLiveChatTemplates()}
                        className="p-1.5 glass-button rounded-lg flex-shrink-0 hover:bg-white/10 transition-colors"
                        title={t('modals:chatDetail.refreshTemplates')}
                      >
                        <RefreshCw className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${templatesLoading ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        onClick={() => setShowTemplates(false)}
                        className="p-1.5 glass-button rounded-lg flex-shrink-0 hover:bg-white/10 transition-colors"
                        title={t('modals:chatDetail.closePanel')}
                      >
                        <ChevronDown className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      </button>
                    </div>

                    {/* 템플릿 칩 목록 */}
                    {templatesLoading ? (
                      <div className="flex justify-center py-3">
                        <div className="spinner w-5 h-5"></div>
                      </div>
                    ) : filteredTemplates.length === 0 ? (
                      <div className="text-center py-3">
                        <p className="text-xs text-dark-500">
                          {templateSearch ? t('common:noSearchResults') : t('modals:chatDetail.noTemplates')}
                        </p>
                        {!templateSearch && (
                          <p className="text-[10px] text-dark-600 mt-1">{t('modals:chatDetail.addTemplateHint')}</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {filteredTemplates.map((template) => (
                          <button
                            key={template.id}
                            onClick={() => handleInsertTemplate(template.content)}
                            className={`group inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-all duration-150 hover:scale-[1.03] active:scale-95 ${
                              template.is_favorite
                                ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/20 hover:bg-yellow-500/20 hover:border-yellow-500/40'
                                : 'bg-white/5 text-white/80 border border-white/10 hover:bg-white/10 hover:border-white/20'
                            }`}
                            title={template.content}
                          >
                            {template.is_favorite && (
                              <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                            )}
                            <span className="max-w-[100px] sm:max-w-[160px] truncate">{template.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}

          {/* Input */}
          {!isClosed && currentStatus !== 'WAITING' && (
            <div className="p-2 sm:p-6 border-t border-white/10 flex-shrink-0 modal-footer-safe">
              <div className="flex items-center gap-1 sm:gap-3 w-full min-w-0">
                {/* 템플릿 토글 버튼 */}
                <button
                  onClick={() => {
                    setShowTemplates(!showTemplates)
                    if (!showTemplates && !templatesLoaded) {
                      loadLiveChatTemplates()
                    }
                  }}
                  className={`p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0 transition-all duration-200 ${
                    showTemplates
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30 shadow-lg shadow-orange-500/10'
                      : 'glass-button hover:bg-white/10'
                  }`}
                  title={t('modals:chatDetail.liveTemplate')}
                >
                  <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>

                <textarea
                  value={messageText}
                  rows={1}
                  onChange={(e) => {
                    setMessageText(e.target.value)
                    // 자동 높이(최대 5줄) — 붙여넣은 여러 줄이 그대로 보이게 (2026-07-29)
                    e.target.style.height = 'auto'
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'
                  }}
                  onKeyDown={(e) => {
                    // Enter=전송 / Shift+Enter=줄바꿈 (2026-07-29: 종전 input이라 줄바꿈 소실됐음)
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage()
                      const el = e.target as HTMLTextAreaElement
                      setTimeout(() => { el.style.height = 'auto' }, 0)
                    }
                  }}
                  placeholder={t('modals:chatDetail.messageInput')}
                  className="input-glass flex-1 min-w-0 text-sm sm:text-base px-2 sm:px-4 py-2 resize-none leading-snug"
                  style={{ maxHeight: 120 }}
                  disabled={sending}
                />

                <label className="glass-button p-2 sm:p-3 rounded-lg sm:rounded-xl cursor-pointer flex-shrink-0">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={sending}
                  />
                  <Image className="w-4 h-4 sm:w-5 sm:h-5" />
                </label>

                <button
                  onClick={handleSendMessage}
                  disabled={!messageText.trim() || sending}
                  className="btn-primary flex items-center disabled:opacity-50 text-sm sm:text-base p-2 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl flex-shrink-0 gap-2"
                >
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">{t('modals:chatDetail.send')}</span>
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* 메시지 우클릭/롱프레스 컨텍스트 메뉴 */}
        {contextMenu && (
          <div
            ref={contextMenuRef}
            className="fixed z-[10000] glass-dark border border-white/10 rounded-lg shadow-xl py-1 min-w-[150px]"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <button
              type="button"
              onClick={handleCopyMessage}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors"
            >
              <Copy className="w-4 h-4" />
              <span>{t('modals:chatDetail.copyMessage', '복사')}</span>
            </button>
            {(() => {
              const target = messages.find(m => m.id === contextMenu.messageId)
              if (!target || target.sender_type !== 'admin' || target.message_type !== 'TEXT') return null
              return (
                <button
                  type="button"
                  onClick={() => {
                    setEditingMsg({ id: target.id, text: target.content || '' })
                    closeContextMenu()
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>{t('modals:chatDetail.editMessage', '수정')}</span>
                </button>
              )
            })()}
            <button
              type="button"
              onClick={handleDeleteMessage}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span>{t('modals:chatDetail.deleteMessage', '메시지 삭제')}</span>
            </button>
          </div>
        )}

        {/* 채팅 종료 확인 다이얼로그 */}
        <ConfirmDialog
          open={closeConfirmOpen}
          title="채팅을 종료하시겠습니까?"
          description={
            room
              ? `${room.user_name || room.user_email || '사용자'} 님의 채팅방을 종료합니다.\n종료 후에는 다시 활성화할 수 없으며, 사용자 측에 시스템 메시지가 전송됩니다.`
              : ''
          }
          confirmText="종료"
          cancelText="취소"
          variant="danger"
          loading={closeLoading}
          onConfirm={handleCloseRoom}
          onCancel={() => {
            if (!closeLoading) setCloseConfirmOpen(false)
          }}
        />
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

export default ChatDetailModal
